<template>
  <el-form
      @submit.prevent="validateSubmit('customerForm')"
      ref="customerForm"
      :model="customer"
      :rules="rules"
      label-position="top"
      novalidate
  >
    <input-section>
      <div class="flex flex-col md:flex-row justify-end">
        <div class="md:w-4/12 m-2">
          <horizontal-button
              :title="$t('actions.saveChanges')"
              isSuccess
              faIcon="save"
              :disabled="isSubmitting"
              :loading="isSubmitting"
          />
        </div>

        <div class="md:w-4/12 m-2" v-if="enableClearForm">
          <horizontal-button
              :title="$t('actions.clearButton')"
              isDanger
              faIcon="trash"
              @click.prevent="clearForm('customerForm')"
              :disabled="isSubmitting"
          />
        </div>
      </div>
    </input-section>

    <!-- @submit errors -->
    <alert-badge
        isDanger
        :title="error.title"
        :body="error.body"
        @dismissed="
        () => {
          error.title = ''
          error.body = ''
        }
      "
    />

    <input-section>
      <h1>{{ t('sections.contact') }}</h1>

      <div class="grid grid-cols-3 gap-2">
        <el-form-item :label="t('name')" prop="name" class="!col-span-2 input-label">
          <el-input
              id="name"
              v-model="customer.name"
              :placeholder="t('placeholders.name')"
              maxlength="127"
              show-word-limit
              clearable
              required
          >
          </el-input>
        </el-form-item>
        <el-form-item :label="t('customerType')" prop="selectedLocationID"
                      class="!col-span-1 input-label">
          <el-select v-model="customer.customerType" filterable clearable
                     class="w-full">
            <el-option
                v-for="type in customPriceTypes"
                :key="type"
                :label="$ctx.locale === 'en' ? type.enName : type.arName"
                :value="type.id"
            >
              <span :class="{ 'float-right': $ctx.getDir() == 'rtl' }">{{
                  $ctx.locale === 'en' ? type.enName : type.arName
                }}</span>
            </el-option>
          </el-select>
        </el-form-item>
      </div>

      <input-group>
        <el-form-item :label="t('phone')" prop="phone" class="input-label">
          <el-input
              id="phone"
              v-model="customer.phone"
              :placeholder="t('placeholders.phone')"
              maxlength="15"
              dir="ltr"
          >
          </el-input>
        </el-form-item>

        <el-form-item :label="t('phone2')" prop="phone2" class="input-label">
          <el-input
              id="phone2"
              v-model="customer.phone2"
              :placeholder="t('placeholders.phone2')"
              maxlength="15"
              dir="ltr"
          >
          </el-input>
        </el-form-item>
      </input-group>

      <div class="grid grid-cols-5 gap-2">
        <el-form-item :label="t('dateOfBirth')" prop="dateOfBirth"
                      class="input-label col-span-4">
          <el-date-picker
              id="dateOfBirth"
              v-model="customer.dateOfBirth"
              type="date"
              format="DD-MM-YYYY"
              placeholder="Pick a date"
              style="width: 100%"
          />
        </el-form-item>

        <div class="el-form-item asterisk-left input-label col-span-1">
          <label class="el-form-item__label">{{t('Age')}}</label>
          <div class="el-form-item__content bg-gray-50 ps-3 rounded-md border border-input">
            {{customer.dateOfBirth ? formatAge(customer.dateOfBirth): '-'}}
          </div>
        </div>
      </div>

      <input-group>
        <el-form-item :label="t('businessName')" prop="businessName"
                      class="input-label">
          <el-input
              id="businessName"
              v-model="customer.businessName"
              :placeholder="t('placeholders.businessName')"
              maxlength="127"
              show-word-limit
              clearable
              required
          >
          </el-input>
        </el-form-item>

        <el-form-item :label="t('email')" prop="email" class="input-label">
          <el-input
              id="email"
              v-model="customer.email"
              type="email"
              :placeholder="t('placeholders.email')"
              dir="ltr"
              maxlength="255"
          >
          </el-input>
        </el-form-item>
      </input-group>
    </input-section>

    <input-section v-if="useSettingsStore().getValue('vet_module') === 'true'" style="width: 100% !important; display: flex; justify-content: center;">
      <customer-pets
        ref="customerPetsRef"
        v-model:pets="customer.customerPets"
        :customer-id="customer.id"
      />
    </input-section>

    <input-section>
      <h1>{{ t('sections.address') }}</h1>
      <el-form-item :label="t('streetAddress')" prop="streetAddress"
                    class="input-label">
        <el-input
            id="streetAddress"
            v-model="customer.streetAddress"
            :placeholder="t('placeholders.streetAddress')"
            show-word-limit
            maxlength="255"
            clearable
        >
        </el-input>
      </el-form-item>

      <input-group>
        <el-form-item :label="t('country')" prop="country" class="input-label">
          <deletable-autocomplete
              id="country"
              v-model="customer.country"
              :placeholder="t('placeholders.country')"
              :fetch-suggestions="countrySearch"
          />
        </el-form-item>
        <el-form-item :label="t('city')" prop="city" class="input-label">
          <deletable-autocomplete
              id="city"
              v-model="customer.city"
              :placeholder="t('placeholders.city')"
              :fetch-suggestions="citySearch"
          />
        </el-form-item>

        <el-form-item :label="t('neighborhood')" prop="neighborhood"
                      class="input-label">
          <deletable-autocomplete
              id="neighborhood"
              v-model="customer.neighborhood"
              :placeholder="t('placeholders.neighborhood')"
              :fetch-suggestions="neighborhoodSearch"
          />
        </el-form-item>

        <el-form-item :label="t('postalCode')" prop="postalCode"
                      class="input-label">
          <el-input
              id="postalCode"
              :placeholder="t('placeholders.postalCode')"
              show-word-limit
              maxlength="10"
              clearable
              v-model="customer.postalCode"
          >
          </el-input>
        </el-form-item>
      </input-group>
      <el-form-item :label="t('notes')" prop="notes" class="input-label">
        <el-input
            id="notes"
            type="textarea"
            :placeholder="t('placeholders.notes')"
            show-word-limit
            clearable
            v-model="customer.notes"
        >
        </el-input>
      </el-form-item>
    </input-section>
  </el-form>
</template>

<script lang="ts">
import {defineComponent} from 'vue'

// components
import AlertBadge from '@/components/AlertBadge.vue'
import HorizontalButton from '@/components/HorizontalButton.vue'
import InputSection from '@/components/form/InputSection.vue'
import InputGroup from '@/components/form/InputGroup.vue'
import DeletableAutocomplete from '@/components/form/DeletableAutocomplete.vue'
import CustomerPets from '@/components/customer/CustomerPets.vue'
import {ElForm, ElMessageBox} from 'element-plus'

// models
import Customer, {customerI18nMessages} from '@/models/customer/Customer'
import {useI18n} from 'vue-i18n'
import CustomPriceType from '@/models/inventory/CustomPrice'
import {useSettingsStore} from "@/stores/settingsStore";
import {formatAge} from "@/utils/date";
import { useStorage } from '@vueuse/core'

interface Suggestion {
  id?: number;
  value: string;
}

interface SuggestionResult {
  value: string;
  id?: number;
}

export default defineComponent({
  name: 'customer-form',

  setup() {
    const {t} = useI18n({
      useScope: 'global',
      messages: {
        en: {
          ...customerI18nMessages.en,

          phoneNumberAlreadyUsedMsg:
              'This phone number has been previously used. Are you sure you want to use it again?',
          sections: {
            customer: 'Customer Info',
            contact: 'Contcat Info',
            address: 'Address'
          },

          placeholders: {
            name: 'Example: Ahmed',
            phone: 'Example: 077011111111',
            phone2: 'Example: 077011111111',
            email: 'Example: info[at]hamza.com',
            streetAddress: 'Example: Next to Almansour Mall',
            city: 'Example: Baghdad',
            postalCode: 'Example: 10015',
            country: 'Example: Iraq',
            businessName: 'Example: Thecky',
            neighborhood: 'Example: Al Jamaa',
            notes: '',
          },
          OK: 'OK',
          Cancel: 'Cancel'
        },
        ar: {
          ...customerI18nMessages.ar,

          phoneNumberAlreadyUsedMsg:
              'تم استخدام رقم الهاتف مسبقا, هل انت متأكد انك تريد استخدامه مرة اخرى؟',
          sections: {
            customer: 'عن الزبون',
            contact: 'معلومات الزبون',
            address: 'العنوان'
          },

          placeholders: {
            name: 'مثلا: احمد',
            phone: 'مثلا: 077011111111',
            phone2: 'مثلا: 077011111111',
            email: `مثلا: info[at]hamaza.com`,
            streetAddress: 'مثلا: مجاور لمول المنصور',
            city: 'مثلا: بغداد',
            postalCode: 'مثلا: 10015',
            country: 'مثلا: العراق',
            businessName: 'مثلا: الذكي',
            neighborhood: 'مثلا: حي الجامعة',
            notes: '',
          },
          OK: 'موافق',
          Cancel: 'الغاء'
        }
      }
    })

    const rules = {
      name: [{max: 127, message: t('validation.maxLength', {len: 127})}],
      phone: [{max: 15, message: t('validation.maxLength', {len: 15})}],
      phone2: [{max: 15, message: t('validation.maxLength', {len: 15})}],
      email: [{max: 255, message: t('validation.maxLength', {len: 255})}],
      streetAddress: [{
        max: 255,
        message: t('validation.maxLength', {len: 255})
      }],
      city: [{max: 70, message: t('validation.maxLength', {len: 70})}],
      postalCode: [{max: 10, message: t('validation.maxLength', {len: 10})}],
      country: [{max: 70, message: t('validation.maxLength', {len: 70})}]
    }

    return {t, rules, useSettingsStore}
  },

  components: {
    AlertBadge,
    HorizontalButton,
    InputSection,
    InputGroup,
    DeletableAutocomplete,
    CustomerPets
  },
  props: {
    initCustomer: {
      type: Customer,
      required: true
    },
    submitAction: {
      type: Function,
      required: true
    },
    successMsg: {
      type: String,
      required: true
    },
    enableClearForm: {
      type: Boolean,
      required: false
    },
    clearFormOnSubmit: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      customer: this.initCustomer as Customer,
      error: {title: '', body: ''},
      customPriceTypes: [] as Array<CustomPriceType>,
      searchTerm: useStorage('customers-search-term'),
      totalResultCount: useStorage('customers-total-result-count'),
      isSubmitting: false,
    }
  },

  mounted() {
    if (!this.initCustomer.id) {
      const res = useSettingsStore().cities.filter(i => i.value = useSettingsStore().getValue("default_city"));
      if (res.length) {
        this.customer.city = res[0].arLabel
      }

      if (this.totalResultCount == 0) {
        this.customer.name = this.searchTerm
      }
    }
  },

  async beforeMount() {
    const customPriceTypesResp = await this.$http.get<CustomPriceType[]>(CustomPriceType.ENDPOINT)

    this.customPriceTypes = customPriceTypesResp.map((cat) => CustomPriceType.from(cat))

    const regularCustomPrice = new CustomPriceType()
    regularCustomPrice.id = 'regular'
    regularCustomPrice.enName = 'Regular'
    regularCustomPrice.arName = 'اعتيادي'

    this.customPriceTypes = [regularCustomPrice, ...this.customPriceTypes]

    if (!this.customer.customerType.length) this.customer.customerType = regularCustomPrice.id
  },

  methods: {
    formatAge,
    async confirmDuplicatedPhone() {
      this.isSubmitting = true;
      const phones = [this.customer.phone, this.customer.phone2]

      const promises = phones.map((phone) =>
          this.$http.get<Customer[]>(Customer.ENDPOINT + `?phone=${encodeURIComponent(phone)}`)
      )

      try {
        const results = await Promise.all(promises)

        for (let i = 0; i < results.length; i++) {
          const customers = results[i]
          if (customers.length > 0) {
            const matchingCustomer = customers.find((customer) => customer.id !== this.customer.id)
            if (matchingCustomer) {
              const confirmed = await ElMessageBox.confirm(
                  this.t('phoneNumberAlreadyUsedMsg'),
                  'Warning',
                  {
                    confirmButtonText: this.t('OK'),
                    cancelButtonText: this.t('Cancel'),
                    type: 'warning'
                  }
              )

              return confirmed // Return true if confirmed, false if canceled
            }
          }
        }

        return true // Return true if no duplicates found
      } catch (error) {
        // Handle errors here
        console.error(error)
        return false // Return false in case of an error
      }
    },

    clearForm(formName: string) {
      const form = this.$refs[formName] as typeof ElForm
      form.resetFields()
    },

    async validateSubmit(formName: string) {
      console.log("this.isSubmitting 1", this.isSubmitting)
      if (this.isSubmitting) {
        console.log("this.isSubmitting 2", this.isSubmitting)
        return false
      }

      const form = this.$refs[formName] as typeof ElForm
      form?.validate(async (valid: boolean) => {
        if (!valid) {
          document.getElementById('main')?.scrollTo({top: 0})
          return false
        }

        // Validate embedded pets (their names are required) before saving.
        const petsRef = this.$refs.customerPetsRef as { validate?: () => boolean } | undefined
        if (petsRef?.validate && !petsRef.validate()) {
          document.querySelector('.pet-name-invalid')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
          return false
        }

        if (await this.confirmDuplicatedPhone()) {
          this.isSubmitting = true
          try {
            const resp = await this.submitAction(this.customer)

            if (resp) {
              this.error.title = resp?.title
              this.error.body = resp?.body
              document.getElementById('main')?.scrollTo({top: 0})
              return false
            } else {
              if (this.clearFormOnSubmit) {
                this.clearForm(formName)
              }

              this.$alertModal.showSuccess({title: this.successMsg, body: ''})
            }
            return true
          } finally {
            this.isSubmitting = false
          }
        }

        return false
      })
    },
    async countrySearch(queryString: string, cb: any) {
      try {
        const response = await this.$http.get<Suggestion[]>(`/helpers/suggestions?model=customers&key=country&query=${queryString}`)
        const result = response.map((r: Suggestion) => {
          return {
            value: r.value,
            id: r.id
          }
        })
        cb(result)
      } catch (e) {
        console.log(e)
        cb([])
      }
    },
    async citySearch(queryString: string, cb: any) {
      try {
        const response = await this.$http.get<Suggestion[]>(`/helpers/suggestions?model=customers&key=city&query=${queryString}`)
        const result: SuggestionResult[] = response.map((r: Suggestion) => {
          return {
            value: r.value,
            id: r.id
          }
        })

        useSettingsStore().cities.forEach(city => {
          if (this.$ctx.locale == 'en') {
            if (!response.some((r: Suggestion) => r.value === city.arLabel)) {
              result.push({value: city.arLabel} as SuggestionResult)
            }
          } else {
            if (!response.some((r: Suggestion) => r.value === city.arLabel)) {
              result.push({value: city.arLabel} as SuggestionResult)
            }
          }
        })

        cb(result)
      } catch (e) {
        console.log(e)
        cb([])
      }
    },
    async neighborhoodSearch(queryString: string, cb: any) {
      try {
        const response = await this.$http.get<Suggestion[]>(`/helpers/suggestions?model=customers&key=neighborhood&query=${queryString}`)
        const result = response.map((r: Suggestion) => {
          return {
            value: r.value,
            id: r.id
          }
        })
        cb(result)
      } catch (e) {
        console.log(e)
        cb([])
      }
    },
  }
})
</script>

<style scoped>
form > h1 {
  @apply text-lg py-3 font-bold;
}

form > h2 {
  @apply py-3 font-bold;
}

formt > h3 {
  @apply py-3 font-bold;
}

form {
  @apply flex flex-col items-center;
}
</style>
